/** `a11yLabel` is the spoken name when the label alone does not say what picking it does. */
export type FacetOption = { value: string; label: string; a11yLabel?: string; };

/**
 * One filter facet as data: its options, its current value, and the patch each choice makes.
 *
 * The shared filter sheet's summary rows (`FacetPanel`) and the applied-filter chips
 * (`AppliedFilterChips`) both render from a list of these, so a row and the chip it produces cannot
 * disagree about what is on or how it reads. `P` is the screen's own patch type: inventory passes
 * `Partial<InventoryFilters>`, the storefront its own.
 */
export type Facet<P> = {
  key: string;
  label: string;
  options: FacetOption[];
  value: string | null;
  /** The resting value. A facet holding it is not applied and gets no chip. */
  restingValue: string | null;
  /** The row's text at the resting value ("All games"). */
  restingLabel: string;
  pick: (value: string | null) => P;
  /** The chip's removal, which returns only this facet to rest. */
  clear: () => P;
  /** Long lists (Set) get a search field on their picker. */
  searchable?: boolean;
  /**
   * Present on a multi-select facet (a storefront's Condition, NM or LP): `values` is what is on and
   * `toggle` adds or removes one. Such a facet ignores `value` and rests when `values` is empty;
   * `pick(null)` is its resting choice and clears every value.
   */
  multi?: { values: string[]; toggle: (value: string) => P; };
};

/**
 * Builds a facet, defaulting `clear` to the resting pick.
 *
 * An applied value the option list does not hold yet (Sets or StockGroups still loading, a value
 * from a deep link) stays listed, so its row and its removable chip never vanish while it still
 * narrows the list.
 */
export function facet<P>(f: Omit<Facet<P>, "clear"> & { clear?: Facet<P>["clear"]; }): Facet<P> {
  const held = f.multi ? f.multi.values : f.value !== null && f.value !== f.restingValue
    ? [f.value]
    : [];
  const missing = held.filter((v) => !f.options.some((o) => o.value === v));
  const options = missing.length
    ? [...f.options, ...missing.map((v) => ({ value: v, label: v }))]
    : f.options;
  return { ...f, options, clear: f.clear ?? (() => f.pick(f.restingValue)) };
}

/** Whether a facet narrows the list away from its resting value. */
export function isFacetApplied<P>(f: Facet<P>): boolean {
  return f.multi
    ? (f.restingValue === null
      ? f.multi.values.length > 0
      : f.multi.values.length !== 1 || f.multi.values[0] !== f.restingValue)
    : f.value !== f.restingValue;
}

const optionLabel = <P>(f: Facet<P>, value: string): string =>
  f.options.find((o) => o.value === value)?.label ?? value;

/** The facet's current value as a row shows it ("NM, LP" for a multi-select facet). */
export function facetValueLabel<P>(f: Facet<P>): string {
  if (f.multi) {
    return f.multi.values.length
      ? f.multi.values.map((v) => optionLabel(f, v)).join(", ")
      : f.restingLabel;
  }
  if (f.value === null) return f.restingLabel;
  return optionLabel(f, f.value);
}

/** The number of applied facets, for a Filters trigger's count. */
export function appliedFacetCount<P>(facets: Facet<P>[]): number {
  return facets.filter(isFacetApplied).length;
}

/**
 * One removable chip per applied value: a single-select facet gives one, a multi-select facet one
 * per value, so each chip removes exactly what it names.
 */
export function appliedChips<P>(
  facets: Facet<P>[],
): { key: string; facetLabel: string; label: string; remove: () => P; }[] {
  return facets.filter(isFacetApplied).flatMap((f) => {
    const multi = f.multi;
    if (!multi) {
      return [{ key: f.key, facetLabel: f.label, label: facetValueLabel(f), remove: f.clear }];
    }
    return multi.values.map((v) => ({
      key: `${f.key}:${v}`,
      facetLabel: f.label,
      label: optionLabel(f, v),
      remove: () => multi.toggle(v),
    }));
  });
}
